import { FC, useRef, useState, useEffect } from 'react'

import { Play, Pause, Volume2, VolumeX, Maximize2 } from 'lucide-react'

type Props = {
  primaryVideoUrl: string
  videoPoster?: string
  setVideoDuration?: (duration: number) => void
}

const SessionVideo: FC<Props> = ({
  primaryVideoUrl,
  videoPoster,
  setVideoDuration
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleTimeUpdate = () => setCurrentTime(video.currentTime)
    const handleLoadedMetadata = () => {
      setDuration(video.duration)
      if (video.duration) {
        setVideoDuration && setVideoDuration(video.duration)
      }
    }
    const handleEnded = () => setIsPlaying(false)

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('ended', handleEnded)

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('ended', handleEnded)
    }
  }, [])

  // Video player handlers
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
      setIsMuted(!isMuted)
    }
  }

  const handleFullscreen = () => {
    if (videoRef.current) {
      videoRef.current.requestFullscreen?.()
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value)
    if (videoRef.current) {
      videoRef.current.currentTime = time
      setCurrentTime(time)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className='bg-white rounded-lg shadow-sm p-4 mb-6 animate-fadeIn'>
      <div className='relative bg-gray-900 rounded-lg overflow-hidden'>
        <video
          ref={videoRef}
          src={primaryVideoUrl}
          poster={videoPoster}
          preload='metadata'
          className='w-full aspect-video object-contain'
          muted={isMuted}
        />
        {!isPlaying && (
          <div
            className='absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer'
            onClick={handlePlayPause}
          >
            <div className='p-3 bg-white/20 backdrop-blur-sm rounded-full'>
              <Play size={32} className='text-white' />
            </div>
          </div>
        )}
        {isPlaying && (
          <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <span className='text-white text-xs'>
                {formatTime(currentTime)}
              </span>
              <input
                type='range'
                min='0'
                max={duration || 0}
                value={currentTime}
                onInput={handleSeek}
                className='flex-1 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer'
              />
              <span className='text-white text-xs'>{formatTime(duration)}</span>
            </div>
            <div className='flex items-center gap-2'>
              <button
                onClick={handlePlayPause}
                className='p-2 bg-white/20 hover:bg-white/30 rounded-full'
              >
                {isPlaying ? (
                  <Pause size={20} className='text-white' />
                ) : (
                  <Play size={20} className='text-white' />
                )}
              </button>
              <button
                onClick={handleMuteToggle}
                className='p-2 bg-white/20 hover:bg-white/30 rounded-full'
              >
                {isMuted ? (
                  <VolumeX size={20} className='text-white' />
                ) : (
                  <Volume2 size={20} className='text-white' />
                )}
              </button>
              <div className='flex-1' />
              <button
                onClick={handleFullscreen}
                className='p-2 bg-white/20 hover:bg-white/30 rounded-full'
              >
                <Maximize2 size={20} className='text-white' />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SessionVideo
